import styled from '@emotion/styled'
import { calcRem } from 'component-library/__styling/style-functions'
import { colors } from 'component-library/__styling/settings'
import { themedValue } from 'component-library/__styling/themed-value'
import { keyframes } from '@emotion/css'
import { HeartIcon } from './assets/HeartIcon'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { createPropFilter } from 'common/emotion'

export const telethonRedColor = '#DE1529'
export const heartIconClassname = 'heart-icon'

export const pulsingHeart = keyframes({
    '0%': {
        transform: 'scale( 1 )',
    },
    '20%': {
        transform: 'scale( .75 )',
    },
    '40%': {
        transform: 'scale( 1 )',
    },
    '60%': {
        transform: 'scale( .75 )',
    },
    '80%': {
        transform: 'scale( 1 )',
    },
    '100%': {
        transform: 'scale( 1 )',
    },
})

export const StyledHeart = styled(HeartIcon)({
    animation: `${pulsingHeart} 1.5s infinite`,
})

export const StyledTelethonCountdown = styled('div')(({ theme }) => ({
    backgroundColor: colors.white,
    position: 'relative',
    display: 'flex',
    fontFamily: theme.fonts.sansSerif,
    fontSize: calcRem(20),
    lineHeight: themedValue(theme, { fallback: 1.05, thewest: 1.154 }),
    height: themedValue(theme, { fallback: calcRem(177), thewest: calcRem(150) }),
    overflow: 'hidden',
    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
    maxWidth: calcRem(1220),

    [`@media (min-width: 660px) and (max-width: 1019px)`]: {
        height: themedValue(theme, { fallback: calcRem(177), thewest: calcRem(215) }),
        borderTopRightRadius: themedValue(theme, { fallback: calcRem(89), thewest: calcRem(108) }),
        borderBottomRightRadius: themedValue(theme, {
            fallback: calcRem(89),
            thewest: calcRem(108),
        }),
    },

    [`@media (max-width: 659px)`]: {
        height: calcRem(400),
        borderTopRightRadius: 0,
        borderBottomRightRadius: calcRem(100),
        borderBottomLeftRadius: calcRem(100),
        zIndex: 1, // zIndex fixes issue with overflow not being hidden over a border radius
    },

    borderTopRightRadius: themedValue(theme, { fallback: calcRem(89), thewest: calcRem(75) }),
    borderBottomRightRadius: themedValue(theme, { fallback: calcRem(89), thewest: calcRem(75) }),
}))

export const StyledTelethonTrianglesLeft = styled('img')({
    marginTop: calcRem(-50),
    marginLeft: calcRem(-75),

    [`@media (max-width: 659px)`]: {
        position: 'absolute',
    },

    [`@media (max-width: 768px)`]: {
        height: calcRem(200),
    },

    [`@media (max-width: 450px)`]: {
        marginTop: calcRem(-80),
    },
})

export const StyledTelethonTrianglesRight = styled('img')<{ offsetForContent: boolean }>(
    ({ offsetForContent, theme }) => ({
        position: 'absolute',
        right: 0,
        marginRight: offsetForContent ? calcRem(-200) : calcRem(-366),
        marginTop: calcRem(-160),
        paddingTop: 0,

        [`@media (min-width: 1020px)`]: {
            marginRight: offsetForContent ? calcRem(-250) : undefined,
            marginTop: offsetForContent ? calcRem(-140) : undefined,
        },

        ...conditionalStyles(theme.kind !== 'sevennews', {
            [`@media (min-width: 1020px) and (max-width: 1210px)`]: {
                display: offsetForContent ? undefined : 'none',
            },
        }),

        ...conditionalStyles(theme.kind !== 'thewest', {
            [`@media (min-width: 660px)`]: {
                display: themedValue(theme, {
                    perthnow: offsetForContent ? undefined : 'none',
                    fallback: undefined,
                }),
                marginRight: offsetForContent ? calcRem(-250) : undefined,
                marginTop: offsetForContent ? calcRem(-100) : undefined,
            },
        }),

        [`@media (min-width: 660px) and (max-width: 1019px)`]: {
            display: offsetForContent ? undefined : 'none',
            marginRight: offsetForContent ? calcRem(-270) : undefined,
            marginTop: offsetForContent ? calcRem(-100) : undefined,
        },

        [`@media (min-width: 451px) and (max-width: 659px)`]: {
            bottom: 0,
            marginBottom: calcRem(-120),
            marginRight: offsetForContent ? calcRem(-270) : undefined,
        },

        [`@media (max-width: 450px)`]: {
            bottom: 0,
            marginBottom: calcRem(-150),
            marginRight: offsetForContent ? calcRem(-270) : undefined,
        },
    })
)

export const StyledTelethonKids = styled('div')(({ theme }) => ({
    position: 'absolute',
    overflow: 'hidden',

    left: calcRem(-60),
    img: {
        width: 335,
    },

    ...conditionalStyles(theme.kind !== 'thewest', {
        [`@media (min-width: 1020px)`]: {
            left: calcRem(-80),
        },
    }),

    // hide below tablet breakpoint
    [`@media (max-width: 767px)`]: {
        display: 'none',
    },
}))

export const Content = styled('div', {
    shouldForwardProp: createPropFilter<JSX.IntrinsicElements['div']>()(['offset']),
})<{ offset: boolean }>(({ offset, theme }) => ({
    display: 'flex',
    flexDirection: theme.kind !== 'thewest' ? 'column' : undefined,

    [`@media (max-width: 1019px)`]: {
        flexDirection: 'column',
    },

    [`@media (min-width: 768px) and (max-width: 1019px)`]: {
        marginLeft: calcRem(40),
        marginRight: offset ? calcRem(90) : undefined,
    },

    ...conditionalStyles(theme.kind !== 'thewest', {
        [`@media (min-width: 1020px)`]: {
            marginLeft: calcRem(-15),
        },
    }),

    [`@media (min-width: 660px) and (max-width: 767px)`]: {
        marginLeft: calcRem(-55),
        marginRight: offset ? calcRem(100) : undefined,
    },

    [`@media (min-width: 660px)`]: {
        paddingRight: calcRem(50),
    },

    [`@media (max-width: 659px)`]: {
        padding: calcRem(50, 10, 10, 10),
        textAlign: 'center',
    },
}))

export const LogoAndInfoContent = styled('div')<{ hiddenCountdown: boolean }>(
    ({ theme, hiddenCountdown }) => ({
        padding: calcRem(15, 0, 0),
        p: {
            margin: themedValue(theme, { fallback: calcRem(10, 0), thewest: calcRem(15, 0) }),

            [`@media (max-width: 659px)`]: {
                margin: calcRem(15, 0),
            },
        },

        // only apply flex basis after 1019 & thewest
        ...conditionalStyles(theme.kind === 'thewest', {
            [`@media (min-width: 1019px)`]: {
                minWidth: calcRem(260),
                flexBasis: calcRem(360),
            },

            [`@media (min-width: 1100px)`]: {
                flexBasis: hiddenCountdown ? calcRem(400) : undefined,
            },

            [`@media (min-width: 1150px)`]: {
                flexBasis: hiddenCountdown ? calcRem(450) : undefined,
            },
        }),

        ...conditionalStyles(theme.kind !== 'thewest', {
            [`@media (min-width: 1019px)`]: {
                width: calcRem(370),
                p: {
                    margin: themedValue(theme, {
                        sevennews: calcRem(7, 0, 5, 0),
                        fallback: undefined,
                    }),
                    fontSize: themedValue(theme, {
                        thewest: calcRem(19),
                        perthnow: calcRem(18.5),
                        sevennews: calcRem(17),
                    }),
                },
            },
        }),
        ...conditionalStyles(theme.kind === 'sevennews', {
            [`@media (min-width: 660px) and (max-width: 915px)`]: {
                p: {
                    fontSize: calcRem(18),
                    margin: calcRem(10, 0),
                },
            },
        }),
    })
)

export const StyledTelethonLogo = styled('img')({
    width: calcRem(240),
    height: calcRem(32),
    marginLeft: calcRem(-23),
})

export const RedText = styled('span')({ color: telethonRedColor, fontWeight: 700 })

export const DonateContent = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginRight: 'auto',
    padding: theme.kind !== 'thewest' ? 0 : calcRem(20),

    [`@media (max-width: 1019px)`]: {
        padding: 0,
    },

    [`@media (max-width: 659px)`]: {
        flexDirection: 'column',
        marginRight: 0,

        a: {
            marginBottom: calcRem(15),
        },
    },
}))

export const DonateNow = styled('a')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    padding: calcRem(10, 20),
    backgroundColor: telethonRedColor,
    color: colors.white,
    width: calcRem(170),
    height: themedValue(theme, { fallback: calcRem(40), thewest: calcRem(47) }),
    borderRadius: themedValue(theme, { fallback: calcRem(20), thewest: calcRem(24) }),
    textDecoration: 'none',
    outline: 'none',
    zIndex: 2, // needs to be higher than the other element thats zIndex 1 to fix an ios issue
    fontSize: themedValue(theme, { fallback: calcRem(20), sevennews: calcRem(16) }),

    [`@media (min-width: 660px)`]: {
        marginRight: themedValue(theme, {
            perthnow: calcRem(5),
            thewest: calcRem(5),
            sevennews: calcRem(15),
        }),
    },

    [`@media (min-width: 769px)`]: {
        marginRight: themedValue(theme, {
            perthnow: calcRem(5),
            thewest: calcRem(30),
            sevennews: calcRem(20),
        }),
    },

    svg: {
        marginRight: calcRem(10),
        path: {
            fill: colors.white,
        },
    },

    ['&:hover']: {
        backgroundColor: '#C00716',
    },

    ['&:focus']: {
        color: telethonRedColor,
        backgroundColor: colors.white,
        border: `solid 1px ${telethonRedColor}`,
        svg: {
            path: {
                fill: telethonRedColor,
            },
        },
    },
}))

export const BoldStyled = styled('span')({
    fontWeight: 700,
})

export const StyledDonateMessage = styled('div')<{ showContent: boolean }>(
    ({ showContent, theme }) => ({
        position: 'absolute',
        zIndex: 100,
        right: 40,
        display: showContent ? 'block' : 'none',
        span: { fontSize: calcRem(18), color: colors.white },
        width: calcRem(100),
        textAlign: 'center',

        [`@media (min-width: 1020px)`]: {
            span: {
                fontSize: themedValue(theme, { fallback: calcRem(17), sevennews: calcRem(15) }),
            },
            top: themedValue(theme, {
                perthnow: calcRem(57),
                thewest: calcRem(30),
                sevennews: calcRem(55),
            }),
            right: calcRem(20),
        },

        [`@media (min-width: 660px) and (max-width: 1019px)`]: {
            span: {
                fontSize: themedValue(theme, { fallback: calcRem(17), sevennews: calcRem(14) }),
            },
            top: themedValue(theme, { fallback: calcRem(65), thewest: calcRem(80) }),
            right: themedValue(theme, { fallback: calcRem(10), thewest: calcRem(15) }),
        },

        [`@media (min-width: 451px) and (max-width: 659px)`]: {
            span: { fontSize: calcRem(16) },
            bottom: 40,
            right: themedValue(theme, { fallback: calcRem(20), sevennews: calcRem(15) }),
        },

        [`@media (max-width: 450px)`]: {
            span: {
                fontSize: themedValue(theme, { fallback: calcRem(16), sevennews: calcRem(14) }),
            },
            bottom: themedValue(theme, { fallback: calcRem(30), sevennews: calcRem(40) }),
            right: themedValue(theme, { fallback: calcRem(15), sevennews: calcRem(10) }),
        },
    })
)
